import { useState } from "react";
import { Loader2, Package, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/store";
import { useDelivery, setDeliveryCheck, setFreightChoice, scopeOf } from "@/lib/delivery";

const Option = ({ svc, insured, opt, chosen, onPick }) => {
  const testid = `freight-${svc.name.toLowerCase()}-${insured ? "with" : "without"}`;
  if (opt.error) return <p data-testid={testid} className="rounded-xl border border-white/10 px-3 py-2 text-xs text-zinc-400">{insured ? "Com seguro" : "Sem seguro"}: {opt.error}</p>;
  return (
    <button type="button" data-testid={testid} aria-pressed={chosen} onClick={onPick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${chosen ? "border-emerald-400 bg-emerald-400/10" : "border-white/10 hover:border-white/30"}`}>
      <span className="flex items-center gap-1.5 text-zinc-200">{insured && <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />}{insured ? "Com seguro" : "Sem seguro"}</span>
      <span className="text-right"><strong className="text-white">{formatPrice(opt.price)}</strong>{opt.days ? <span className="block text-xs text-zinc-400">{opt.days} dias úteis</span> : null}</span>
    </button>
  );
};

// Frete pelos Correios (PAC/SEDEX, com e sem seguro) para um CEP. Dentro do raio mostra entrega grátis.
export const FreightQuote = ({ items, testid = "freight-quote", stacked = false }) => {
  const d = useDelivery();
  const [cep, setCep] = useState(d.cep?.length === 8 ? `${d.cep.slice(0, 5)}-${d.cep.slice(5)}` : d.cep || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data: res } = await api.post("/freight/quote", { cep, items });
      setData(res ?? null);
      if (res?.delivery) setDeliveryCheck(res.delivery, res.cep);
    } catch (err) {
      setData(null);
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const quotes = Array.isArray(data?.quotes) ? data.quotes : [];
  const pick = (svc, insured) => setFreightChoice({ service: svc.name, insured, price: svc[insured ? "with" : "without"].price, days: svc[insured ? "with" : "without"].days, scope: scopeOf(items) });
  const isChosen = (svc, insured) => d.freight?.service === svc.name && !!d.freight?.insured === insured;

  return (
    <div data-testid={testid} className="mt-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Package className="h-4 w-4 text-zinc-300" /> Frete pelos Correios</p>
      <form onSubmit={submit} className="mt-2 flex gap-2">
        <input data-testid={`${testid}-cep`} inputMode="numeric" value={cep} placeholder="Seu CEP" maxLength={9}
          onChange={(e) => setCep(e.target.value.replace(/[^\d-]/g, ""))}
          className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-white/40" />
        <button data-testid={`${testid}-submit`} disabled={busy} className="flex shrink-0 items-center rounded-full border border-white/20 px-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black disabled:opacity-70">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calcular frete"}
        </button>
      </form>
      {error && <p data-testid={`${testid}-error`} className="mt-2 text-sm text-amber-300">{error}</p>}
      {data?.delivery?.available && (
        <p data-testid={`${testid}-free`} className="mt-3 flex items-start gap-2 rounded-2xl bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> Entrega grátis em {data.delivery.city.name} - {data.delivery.city.uf} ({data.delivery.distance_km} km da loja).
        </p>
      )}
      {quotes.length > 0 && (
        <div data-testid={`${testid}-results`} className="mt-3 space-y-3">
          <p className="text-xs text-zinc-400">{data.delivery.city.name} - {data.delivery.city.uf}. Toque numa opção para ela ir no pedido do WhatsApp.</p>
          {quotes.map((svc) => (
            <div key={svc.id}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-300">{svc.name}</p>
              <div className={`grid grid-cols-1 gap-2 ${stacked ? "" : "sm:grid-cols-2"}`}>
                <Option svc={svc} insured={false} opt={svc.without} chosen={isChosen(svc, false)} onPick={() => pick(svc, false)} />
                <Option svc={svc} insured opt={svc.with} chosen={isChosen(svc, true)} onPick={() => pick(svc, true)} />
              </div>
            </div>
          ))}
          <p className="text-xs text-zinc-400">Com seguro: valor declarado igual ao preço do aparelho. Sem seguro: sem valor declarado.</p>
        </div>
      )}
    </div>
  );
};
